import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, Printer, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import JsBarcode from "jsbarcode";
import {
  trackerGetJob,
  trackerListTeam,
  STATUS_COLORS,
  PRIORITY_COLORS,
  QUEUE_COLORS,
  CUSTOMER_TYPE_COLORS,
  PRODUCT_TAG_COLORS,
} from "../lib/tracker";
import { getAdminToken } from "../lib/admin";

const fmtUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "2-digit", year: "numeric",
    });
  } catch { return iso; }
};

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

const CHECKBOX_ITEMS = [
  "Printed",
  "Quality Checked",
  "Finishing Done",
  "Sample Saved",
  "Packing Complete",
  "Customer Review Requested",
  "Informed Customer",
  "Updated Trackers",
  "Charged Customer",
];

const JobTicket = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const format = (searchParams.get("format") || "letter").toLowerCase();
  const is4x6 = format === "4x6";
  const [job, setJob] = useState(null);
  const [members, setMembers] = useState({});
  const [error, setError] = useState("");
  const printedOnce = useRef(false);

  useEffect(() => {
    if (!getAdminToken()) {
      setError("Not authenticated. Please sign in to the admin panel first, then re-open this ticket.");
      return;
    }
    (async () => {
      try {
        const [j, team] = await Promise.all([trackerGetJob(id), trackerListTeam()]);
        setJob(j);
        const m = {};
        team.forEach((t) => { m[t.id] = t.name; });
        setMembers(m);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load job");
      }
    })();
  }, [id]);

  // Auto-trigger print dialog once the ticket has rendered
  useEffect(() => {
    if (!job || printedOnce.current) return;
    printedOnce.current = true;
    const t = setTimeout(() => { window.print(); }, 350);
    return () => clearTimeout(t);
  }, [job]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-stone-100">
        <div className="card-swiss p-6 max-w-md text-sm" data-testid="job-ticket-error">
          <div className="font-bold mb-2 flex items-center gap-2"><X size={16} /> Couldn’t load ticket</div>
          <div className="text-[color:var(--ups-subtext)]">{error}</div>
        </div>
      </div>
    );
  }
  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Loader2 className="animate-spin mr-2" size={16} /> Loading ticket…
      </div>
    );
  }

  const priC = PRIORITY_COLORS[job.priority] || PRIORITY_COLORS.Normal;
  const queueC = job.queue ? (QUEUE_COLORS[job.queue] || { bg: "#E7E5E4", fg: "#1C1917", border: "#A8A29E" }) : null;
  const ctC = job.customer_type ? (CUSTOMER_TYPE_COLORS[job.customer_type] || { bg: "#737373", fg: "#FFFFFF" }) : null;
  const stC = STATUS_COLORS[job.status] || { bg: "#E7E5E4", fg: "#1C1917", border: "#A8A29E" };
  const owners = (job.owner_ids || []).map((o) => members[o] || "?").join(", ");
  const sales = members[job.sales_poc_id] || "";

  // Latest 3 notes (chronological order — newest at top of "Notes" block)
  const latestNotes = [...(job.notes || [])].slice(-3).reverse();

  // Description capped to ~600 chars to keep the ticket on a single page
  const descTrimmed = (job.description || "").slice(0, 600);
  const descTruncated = (job.description || "").length > 600;

  // ─────────────────────────────────────────────────────────────────────
  // 4×6 THERMAL TICKET (early-return when ?format=4x6)
  // ─────────────────────────────────────────────────────────────────────
  if (is4x6) {
    return <Ticket4x6 job={job} owners={owners} />;
  }

  return (
    <div className="ticket-root">
      {/* Print-only stylesheet */}
      <style>{`
        @page { size: Letter; margin: 0.4in; }
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .ticket-page { box-shadow: none !important; border: none !important; margin: 0 !important; padding: 0 !important; }
        }
        .ticket-page { font-family: 'IBM Plex Sans', system-ui, sans-serif; color: #1C1917; }
        .ticket-page .mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .ticket-page h1, .ticket-page h2, .ticket-page h3 { font-family: 'IBM Plex Sans Condensed', 'IBM Plex Sans', system-ui, sans-serif; }
        .field-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.16em; color: #737373; margin-bottom: 2px; }
        .field-value { font-size: 13px; font-weight: 600; }
        .pill { display: inline-block; padding: 3px 8px; font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; border-radius: 3px; }
        .checkbox-row { display: flex; align-items: center; gap: 10px; padding: 6px 10px; border: 1px solid #1C1917; border-radius: 3px; font-size: 12px; font-weight: 700; }
        .checkbox-box { width: 16px; height: 16px; border: 2px solid #1C1917; border-radius: 2px; flex-shrink: 0; }
      `}</style>

      <div className="no-print fixed top-3 right-3 z-50 flex gap-2">
        <button
          onClick={() => window.print()}
          className="btn-brutal px-3 py-2 rounded-sm text-[12px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5"
          data-testid="ticket-print-btn"
        >
          <Printer size={14} /> Print
        </button>
        <button
          onClick={() => window.close()}
          className="px-3 py-2 rounded-sm text-[12px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 bg-white"
          style={{ border: "1px solid var(--ups-border)" }}
        >
          <X size={14} /> Close
        </button>
      </div>

      <div
        className="ticket-page max-w-[7.7in] mx-auto my-6 bg-white p-8"
        style={{ border: "1px solid #1C1917", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
        data-testid="job-ticket"
      >
        {/* Header bar */}
        <div className="flex items-start justify-between gap-4 pb-4 mb-4" style={{ borderBottom: "3px solid #1C1917" }}>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">Job Ticket · Roswell Print Center</div>
            <h1 className="text-3xl font-black tracking-tight leading-tight mt-1">{job.name}</h1>
            <div className="mt-1 flex items-center gap-3 flex-wrap">
              <span className="mono text-sm font-bold">{job.job_number}</span>
              {job.order_number && <span className="mono text-[11px] text-stone-500">Order #{job.order_number}</span>}
              <span className="pill" style={{ background: stC.bg, color: stC.fg, border: `1px solid ${stC.border}` }}>{job.status}</span>
            </div>
          </div>
          {/* Highlighted Customer Type + Priority + Due — all together */}
          <div
            className="shrink-0 rounded-sm overflow-hidden"
            style={{ border: "3px solid #1C1917", minWidth: 250 }}
            data-testid="ticket-highlight-block"
          >
            {ctC ? (
              <div
                className="px-3 py-1.5 text-center"
                style={{ background: ctC.bg, color: ctC.fg }}
                data-testid="ticket-customer-type"
              >
                <div className="text-[8px] font-bold uppercase tracking-[0.2em] opacity-80">Customer Type</div>
                <div className="text-base font-black tracking-tight leading-none mt-0.5">{job.customer_type}</div>
              </div>
            ) : (
              <div className="px-3 py-1.5 text-center bg-stone-100 text-stone-500">
                <div className="text-[8px] font-bold uppercase tracking-[0.2em]">Customer Type</div>
                <div className="text-sm font-bold">— not set —</div>
              </div>
            )}
            <div className="grid grid-cols-2" style={{ borderTop: "2px solid #1C1917" }}>
              <div
                className="px-3 py-2 text-center"
                style={{ background: priC.bg, color: priC.fg, borderRight: "2px solid #1C1917" }}
                data-testid="ticket-priority"
              >
                <div className="text-[8px] font-bold uppercase tracking-[0.18em] opacity-80">Priority</div>
                <div className="text-lg font-black tracking-tight leading-none mt-0.5">{job.priority}</div>
              </div>
              <div
                className="px-3 py-2 text-center"
                style={{ background: "#FEF3C7", color: "#78350F" }}
                data-testid="ticket-due"
              >
                <div className="text-[8px] font-bold uppercase tracking-[0.18em] opacity-90">Due</div>
                <div className="text-sm font-black leading-tight mt-0.5">{fmtDate(job.due_at)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Products being printed */}
        {(job.products || []).length > 0 && (
          <div
            className="mb-4 rounded-sm py-3 px-4 flex items-start justify-between gap-3"
            style={{ background: "#FAFAF9", border: "2px solid #1C1917" }}
            data-testid="ticket-products"
          >
            <div className="min-w-0">
              <div className="text-[9px] font-bold uppercase tracking-[0.2em] mb-1.5" style={{ color: "#737373" }}>
                Product{job.products.length === 1 ? "" : "s"}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {job.products.map((p) => {
                  const c = PRODUCT_TAG_COLORS[p] || PRODUCT_TAG_COLORS.Other || { bg: "#A8A29E", fg: "#1C1917" };
                  return (
                    <span
                      key={p}
                      className="px-2.5 py-1 text-[12px] font-black uppercase tracking-[0.12em] rounded-sm"
                      style={{ background: c.bg, color: c.fg, border: "2px solid #1C1917" }}
                      data-testid={`ticket-product-${p.replace(/\W+/g, "-").toLowerCase()}`}
                    >
                      {p}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* QUEUE — large + colored */}
        {queueC ? (
          <div
            className="mb-4 rounded-sm py-4 px-5 flex items-center justify-between"
            style={{ background: queueC.bg, color: queueC.fg, border: `3px solid ${queueC.border}` }}
            data-testid="ticket-queue"
          >
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">Production Queue</div>
              <div className="text-3xl font-black tracking-tight leading-none mt-1">{job.queue}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">Owners</div>
              <div className="text-sm font-bold mt-0.5">{owners || "Unassigned"}</div>
            </div>
          </div>
        ) : (
          <div className="mb-4 rounded-sm py-3 px-4" style={{ background: "#FEE2E2", border: "2px dashed #991B1B", color: "#991B1B" }}>
            <span className="text-[11px] font-bold uppercase tracking-[0.16em]">No queue assigned</span>
          </div>
        )}

        {/* Customer + financials grid */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="p-3 rounded-sm" style={{ border: "1px solid #1C1917" }}>
            <div className="field-label">Customer</div>
            <div className="field-value">{job.customer_name || "—"}</div>
            {job.customer_email && <div className="text-[11px] mono text-stone-600 mt-1">{job.customer_email}</div>}
            <div className="grid grid-cols-2 gap-2 mt-3">
              <div>
                <div className="field-label">Received</div>
                <div className="text-[12px] mono">{fmtDate(job.received_at)}</div>
              </div>
              <div>
                <div className="field-label">Sales POC</div>
                <div className="text-[12px]">{sales || "—"}</div>
              </div>
            </div>
          </div>
          <div className="p-3 rounded-sm" style={{ background: "#1C1917", color: "#FAFAF9" }} data-testid="ticket-financials">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="field-label" style={{ color: "#FFB500" }}>Total Amount</div>
                <div className="text-3xl font-black tracking-tight" style={{ color: "#FFB500" }}>{fmtUSD(job.total_cost)}</div>
              </div>
              <div>
                <div className="field-label" style={{ color: "#A8A29E" }}>Payment Status</div>
                <div className="text-base font-bold mt-1">{job.payment_status}</div>
                <div className="mt-2 inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm"
                  style={{
                    background: job.payment_status === "Not Paid" ? "#DC2626" : "#15803D",
                    color: "#FAFAF9",
                  }}>
                  {job.payment_status === "Not Paid" ? "● Outstanding" : "● Settled"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        {descTrimmed && (
          <div className="p-3 rounded-sm mb-4" style={{ border: "1px solid #1C1917" }}>
            <div className="field-label mb-1">Description</div>
            <div className="text-[12px] leading-relaxed whitespace-pre-wrap">
              {descTrimmed}
              {descTruncated && <em className="text-stone-500"> … (truncated)</em>}
            </div>
          </div>
        )}

        {/* Latest notes */}
        {latestNotes.length > 0 && (
          <div className="p-3 rounded-sm mb-4" style={{ border: "1px solid #1C1917", background: "#FAFAF9" }} data-testid="ticket-notes">
            <div className="field-label mb-1.5">Latest Notes ({latestNotes.length} of {(job.notes || []).length})</div>
            <div className="space-y-2">
              {latestNotes.map((n) => {
                const txt = stripHtml(n.body_html);
                const trimmed = txt.length > 240 ? txt.slice(0, 240) + "…" : txt;
                return (
                  <div key={n.id} className="text-[11px] leading-snug border-l-2 pl-2" style={{ borderColor: "#FFB500" }}>
                    <div className="mono text-[10px] text-stone-500">
                      {n.author_name || "—"} · {n.created_at ? new Date(n.created_at).toLocaleDateString("en-US", { month: "short", day: "2-digit" }) : ""}
                    </div>
                    <div>{trimmed}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Checkboxes */}
        <div className="pt-3 mt-3" style={{ borderTop: "2px solid #1C1917" }} data-testid="ticket-checklist">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2">Production Checklist</div>
          <div className="grid grid-cols-2 gap-2">
            {CHECKBOX_ITEMS.map((label) => (
              <div key={label} className="checkbox-row" data-testid={`ticket-checkbox-${label.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="checkbox-box" />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Review QR Code — promote customer reviews on pickup */}
        <div
          className="mt-4 pt-3 flex items-center justify-between gap-4"
          style={{ borderTop: "2px solid #1C1917" }}
          data-testid="ticket-review-qr"
        >
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-1" style={{ color: "#92400E" }}>
              ⭐ Help us grow
            </div>
            <div className="font-display text-base font-black leading-tight">
              Please Review The UPS Store 3423
            </div>
            <div className="text-[11px] text-stone-700 mt-1 leading-snug">
              Scan this QR code with your phone — it takes less than a minute and means the world to our team.
            </div>
          </div>
          <img
            src="/ups-review-qr.png"
            alt="Scan to review The UPS Store 3423"
            style={{ width: 84, height: 84, border: "2px solid #1C1917", padding: 2, background: "#fff" }}
            data-testid="ticket-review-qr-image"
          />
        </div>

        {/* Footer */}
        <div className="mt-4 pt-2 flex justify-between text-[9px] mono text-stone-400" style={{ borderTop: "1px dashed #A8A29E" }}>
          <span>Roswell Print Center · {job.job_number}</span>
          <span>Generated {new Date().toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" })}</span>
        </div>
      </div>
    </div>
  );
};

export default JobTicket;

/* =====================================================================
 * 4×6 THERMAL TICKET — fits a Zebra / Rollo / Munbyn label printer.
 * Monochrome (1-bit), bold borders, Code128 barcode top, QR bottom →
 * /admin/jobs?focus={id} so the floor team scans → drawer opens.
 * ===================================================================*/
const fmtDateShort = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "2-digit" });
  } catch { return iso; }
};

const Ticket4x6 = ({ job, owners }) => {
  const barcodeRef = useRef(null);
  const printedOnce = useRef(false);

  // Render the Code128 barcode once the SVG is mounted
  useEffect(() => {
    if (!barcodeRef.current) return;
    try {
      JsBarcode(barcodeRef.current, job.job_number || job.id?.slice(0, 8) || "—", {
        format: "CODE128",
        displayValue: false,
        height: 40,
        width: 1.6,
        margin: 0,
        background: "transparent",
        lineColor: "#000",
      });
    } catch (e) {
      // ignore — barcode is decorative, ticket is still readable
    }
  }, [job.job_number, job.id]);

  // Auto-fire the print dialog once rendered
  useEffect(() => {
    if (printedOnce.current) return;
    printedOnce.current = true;
    const t = setTimeout(() => { window.print(); }, 400);
    return () => clearTimeout(t);
  }, []);

  // Latest 1 note, plain-text, capped
  const latestNote = (() => {
    const ns = job.notes || [];
    if (!ns.length) return "";
    const txt = stripHtml(ns[ns.length - 1].body_html || "");
    return txt.length > 120 ? txt.slice(0, 117) + "…" : txt;
  })();

  // Description: 2 short lines max
  const descShort = (() => {
    const d = (job.description || "").trim();
    if (!d) return "";
    return d.length > 140 ? d.slice(0, 137) + "…" : d;
  })();

  // QR opens the tracker drawer on whichever device scans it
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const qrUrl = `${origin}/admin?focus=${encodeURIComponent(job.id)}`;

  const CHECKBOX_4X6 = ["Printed", "Quality Checked", "Packed", "Picked Up"];

  return (
    <div className="thermal-root" data-testid="job-ticket-4x6">
      <style>{`
        @page { size: 4in 6in; margin: 0.12in; }
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; margin: 0 !important; }
          html, body { width: 4in; }
          .thermal-page { box-shadow: none !important; margin: 0 !important; }
        }
        .thermal-page {
          width: 3.75in;
          min-height: 5.75in;
          margin: 0.25in auto;
          background: #fff;
          color: #000;
          font-family: 'IBM Plex Sans Condensed', 'Helvetica Neue', Helvetica, Arial, sans-serif;
          padding: 8px;
          font-size: 10pt;
          line-height: 1.15;
          letter-spacing: 0.01em;
          border: 1px solid #000;
        }
        .thermal-page .mono { font-family: ui-monospace, 'Courier New', monospace; }
        .thermal-page .header {
          border-bottom: 2px solid #000;
          padding-bottom: 4px;
          margin-bottom: 6px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 6px;
        }
        .thermal-page .header-text {
          font-size: 8pt;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.16em;
        }
        .thermal-page .job-name {
          font-size: 17pt;
          font-weight: 900;
          line-height: 1;
          margin: 4px 0 6px;
          letter-spacing: -0.01em;
        }
        .thermal-page .top-blocks { display: flex; gap: 4px; margin-bottom: 6px; }
        .thermal-page .top-block {
          flex: 1;
          border: 1.5px solid #000;
          padding: 4px 6px;
        }
        .thermal-page .top-block.inverted { background: #000; color: #fff; }
        .thermal-page .top-block .lbl { font-size: 7pt; font-weight: 800; text-transform: uppercase; letter-spacing: 0.18em; opacity: 0.8; }
        .thermal-page .top-block .val { font-size: 12pt; font-weight: 900; line-height: 1.05; margin-top: 1px; }
        .thermal-page .queue-band {
          background: #000; color: #fff;
          text-align: center;
          padding: 4px 6px;
          font-size: 13pt;
          font-weight: 900;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .thermal-page .sec-lbl {
          font-size: 7pt;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.18em;
          margin-bottom: 1px;
          border-bottom: 1px dashed #000;
          padding-bottom: 1px;
        }
        .thermal-page .sec-body { font-size: 9pt; margin-bottom: 4px; }
        .thermal-page .checklist {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 3px 6px;
          border: 1.5px solid #000;
          padding: 5px;
          margin-top: 5px;
        }
        .thermal-page .check-item {
          display: flex; align-items: center; gap: 5px;
          font-size: 9pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
        }
        .thermal-page .check-box {
          width: 12px; height: 12px;
          border: 1.5px solid #000;
          flex-shrink: 0;
        }
        .thermal-page .qr-row {
          display: flex; align-items: center; gap: 8px;
          margin-top: 6px;
          padding-top: 5px;
          border-top: 1px dashed #000;
        }
        .thermal-page .footer {
          margin-top: 6px;
          font-size: 7pt;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          display: flex; justify-content: space-between;
          opacity: 0.7;
        }
      `}</style>

      {/* On-screen print controls (hidden from the printout) */}
      <div className="no-print fixed top-3 right-3 z-50 flex gap-2">
        <button
          onClick={() => window.print()}
          className="btn-brutal px-3 py-2 rounded-sm text-[12px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5"
          data-testid="ticket-4x6-print-btn"
        >
          <Printer size={14} /> Print 4×6
        </button>
        <button
          onClick={() => window.close()}
          className="px-3 py-2 rounded-sm text-[12px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 bg-white"
          style={{ border: "1px solid var(--ups-border)" }}
        >
          <X size={14} /> Close
        </button>
      </div>

      <div className="thermal-page">
        {/* Header — name + Code128 barcode */}
        <div className="header">
          <div>
            <div className="header-text">Roswell Printhub · Ticket</div>
            <div className="mono" style={{ fontSize: "11pt", fontWeight: 800 }}>{job.job_number}</div>
          </div>
          <svg ref={barcodeRef} style={{ width: "1.6in", height: "0.45in" }} />
        </div>

        {/* Job name */}
        <div className="job-name">{job.name || "Untitled job"}</div>

        {/* Due / Priority */}
        <div className="top-blocks">
          <div className="top-block">
            <div className="lbl">Due</div>
            <div className="val">{fmtDateShort(job.due_at)}</div>
          </div>
          <div className={`top-block ${["High", "Rush", "Critical"].includes(job.priority) ? "inverted" : ""}`}>
            <div className="lbl">Priority</div>
            <div className="val">{(job.priority || "Normal").toUpperCase()}</div>
          </div>
        </div>

        {/* Queue band */}
        <div className="queue-band">{job.queue || "No queue"}</div>

        {/* Customer */}
        <div className="sec-lbl">Customer</div>
        <div className="sec-body">
          <div style={{ fontWeight: 700 }}>
            {job.customer_name || "—"}
            {job.customer_type ? <span style={{ marginLeft: 6, fontWeight: 600, fontSize: "8pt", textTransform: "uppercase", letterSpacing: "0.1em" }}>· {job.customer_type}</span> : null}
          </div>
          {job.customer_email && <div className="mono" style={{ fontSize: "8pt" }}>{job.customer_email}</div>}
        </div>

        {/* Items / Description */}
        {descShort && (
          <>
            <div className="sec-lbl">Items / Spec</div>
            <div className="sec-body">{descShort}</div>
          </>
        )}

        {/* Latest note */}
        {latestNote && (
          <>
            <div className="sec-lbl">Latest Note</div>
            <div className="sec-body" style={{ fontStyle: "italic" }}>{latestNote}</div>
          </>
        )}

        {/* Status / Total band */}
        <div style={{ border: "1.5px solid #000", padding: "3px 6px", marginTop: 4, display: "flex", justifyContent: "space-between", fontSize: "9pt", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          <span>Status: {job.status}</span>
          <span>{fmtUSD(job.total_cost)} · {(job.payment_status || "—").toUpperCase()}</span>
        </div>

        {/* Station checklist */}
        <div className="checklist">
          {CHECKBOX_4X6.map((label) => (
            <div key={label} className="check-item"><span className="check-box" /> {label}</div>
          ))}
        </div>

        {/* QR + footer caption */}
        <div className="qr-row">
          <QRCodeSVG value={qrUrl} size={68} level="M" includeMargin={false} />
          <div style={{ fontSize: "8pt", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", lineHeight: 1.2 }}>
            Scan to open this job<br />
            <span style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, opacity: 0.7, fontSize: "7.5pt" }}>
              Updates status from the floor
            </span>
            {owners && (
              <div className="mono" style={{ fontSize: "7pt", marginTop: 3, opacity: 0.8 }}>Owners: {owners}</div>
            )}
          </div>
        </div>

        {/* Audit footer */}
        <div className="footer">
          <span>v1 · {job.job_number}</span>
          <span>{new Date().toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
        </div>
      </div>
    </div>
  );
};
